import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getProfile, updateProfile } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { User, Save, Clock, CheckCircle, XCircle, AlertCircle, TrendingUp, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const CHART_COLORS = ['#D3735A', '#3F6352', '#4A788C', '#D4A017', '#B74134', '#2C5545'];

function StatusBadge({ status }) {
  const styles = {
    pending: 'bg-[#D4A017]/10 text-[#D4A017] border border-[#D4A017]/20',
    approved: 'bg-[#2C5545]/10 text-[#2C5545] border border-[#2C5545]/20',
    rejected: 'bg-[#B74134]/10 text-[#B74134] border border-[#B74134]/20',
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold ${styles[status] || styles.pending}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export default function ProfilePage() {
  const { user: authUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const { data } = await getProfile();
      setProfile(data);
      setEditName(data.user?.name || '');
      setEditPhone(data.user?.phone || '');
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({ name: editName, phone: editPhone });
      toast.success('Profile updated');
      setEditing(false);
      loadProfile();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="animate-fade-in space-y-6" data-testid="profile-loading">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white border border-[#E8E3DD] rounded-xl p-6 animate-pulse">
            <div className="h-6 bg-[#F2EFEB] rounded w-48 mb-4" />
            <div className="h-4 bg-[#F2EFEB] rounded w-32" />
          </div>
        ))}
      </div>
    );
  }

  const u = profile?.user || {};
  const stats = profile?.stats || {};
  const serviceData = Object.entries(stats.by_service_type || {}).map(([name, hours]) => ({ name, hours }));

  return (
    <div className="animate-fade-in" data-testid="profile-page">
      <div className="mb-8">
        <h1 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] tracking-tight">My Profile</h1>
        <p className="text-[#5C5855] mt-1">View and manage your volunteer profile</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="bg-white border border-[#E8E3DD] rounded-xl p-6" data-testid="profile-card">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-[#D3735A]/10 flex items-center justify-center">
              <User className="h-8 w-8 text-[#D3735A]" />
            </div>
            <div>
              <h2 className="font-['Manrope'] text-xl font-bold text-[#2D2B2A]" data-testid="profile-name">{u.name}</h2>
              <p className="text-sm text-[#5C5855]">{u.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs font-bold text-[#D3735A] uppercase bg-[#D3735A]/10 px-2 py-0.5 rounded-full">{u.role}</span>
                {u.volunteer_id && <span className="text-xs text-[#8A847F] font-mono" data-testid="profile-volunteer-id">{u.volunteer_id}</span>}
              </div>
            </div>
          </div>

          {editing ? (
            <div className="space-y-4">
              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Name</Label>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="border-[#D1C9C0]" data-testid="edit-name-input" />
              </div>
              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847F]" />
                  <Input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="(555) 123-4567" className="pl-10 border-[#D1C9C0]" data-testid="edit-phone-input" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={saving} className="bg-[#D3735A] hover:bg-[#BD6047] text-white" data-testid="save-profile-btn">
                  <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)} className="border-[#D1C9C0]">Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {u.phone && (
                <div className="flex items-center gap-2 text-sm text-[#5C5855]">
                  <Phone className="h-4 w-4" /> {u.phone}
                </div>
              )}
              {(u.committees || []).length > 0 && (
                <div>
                  <p className="text-xs font-bold text-[#5C5855] uppercase tracking-wider mb-1">Committees</p>
                  <div className="flex flex-wrap gap-1">
                    {u.committees.map(c => (
                      <span key={c} className="text-xs bg-[#3F6352]/10 text-[#3F6352] border border-[#3F6352]/20 px-2 py-0.5 rounded-full">{c}</span>
                    ))}
                  </div>
                </div>
              )}
              <Button variant="outline" onClick={() => setEditing(true)} className="border-[#D1C9C0] mt-2" data-testid="edit-profile-btn">
                Edit Profile
              </Button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MiniStat icon={TrendingUp} label="Total Hours" value={stats.total_approved_hours || 0} color="#3F6352" />
            <MiniStat icon={AlertCircle} label="Pending" value={stats.pending || 0} color="#D4A017" />
            <MiniStat icon={CheckCircle} label="Approved" value={stats.approved || 0} color="#2C5545" />
            <MiniStat icon={XCircle} label="Rejected" value={stats.rejected || 0} color="#B74134" />
          </div>

          {/* Hours by Service Chart */}
          {serviceData.length > 0 && (
            <div className="bg-white border border-[#E8E3DD] rounded-xl p-6" data-testid="profile-service-chart">
              <h3 className="font-['Manrope'] text-lg font-bold text-[#2D2B2A] mb-4">Hours by Service Type</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={serviceData} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E3DD" />
                  <XAxis type="number" tick={{ fontSize: 12, fill: '#5C5855' }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#5C5855' }} width={80} />
                  <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E8E3DD', borderRadius: 8 }} />
                  <Bar dataKey="hours" radius={[0, 6, 6, 0]}>
                    {serviceData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Recent Hours Table */}
      <div className="mt-6">
        <h3 className="font-['Manrope'] text-lg font-bold text-[#2D2B2A] mb-4">Recent Hours</h3>
        <div className="w-full rounded-xl border border-[#E8E3DD] overflow-hidden bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FAF8F5] border-b border-[#E8E3DD]">
                <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Date</TableHead>
                <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Service Type</TableHead>
                <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Hours</TableHead>
                <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Status</TableHead>
                <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(profile?.recent_hours || []).length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#8A847F] text-sm">No hours recorded yet</TableCell></TableRow>
              ) : (
                profile.recent_hours.map((log, i) => (
                  <TableRow key={i} className="border-b border-[#E8E3DD] hover:bg-[#F2EFEB]/50">
                    <TableCell className="px-6 py-4 text-sm text-[#2D2B2A]">{log.date}</TableCell>
                    <TableCell className="px-6 py-4 text-sm text-[#2D2B2A]">{log.service_type}</TableCell>
                    <TableCell className="px-6 py-4 text-sm font-semibold text-[#2D2B2A]">{log.hours}</TableCell>
                    <TableCell className="px-6 py-4"><StatusBadge status={log.status} /></TableCell>
                    <TableCell className="px-6 py-4 text-sm text-[#5C5855] max-w-[200px] truncate">{log.description || '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-[#E8E3DD] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-xs tracking-[0.05em] uppercase font-bold text-[#5C5855]">{label}</span>
      </div>
      <p className="text-2xl font-bold text-[#2D2B2A] font-['Manrope']">{value}</p>
    </div>
  );
}
